/**
 * Normaliza la respuesta del endpoint de iteración por chat.
 *
 * El backend puede devolver:
 *  - un objeto con `reply` + los campos del módulo (contrato actual);
 *  - un objeto con `module_state` (contrato nuevo);
 *  - un string plano (fallback de errores);
 *  - un objeto antiguo con `reply` conteniendo JSON como string.
 *
 * La UI debe consumir siempre esta función para evitar que un cambio de
 * contrato deje los bloques de salida desactualizados.
 */

export interface IterationAlert {
  code?: string;
  message: string;
  severity?: string;
}

export interface IterationResult<T = Record<string, unknown>> {
  reply: string;
  state?: T;
  history?: unknown[];
  changed: boolean;
  alerts: string[];
  alertDetails: IterationAlert[];
}

const META_KEYS = new Set([
  'reply',
  'history',
  'changed',
  'module_id',
  'module_state',
  'guardrail_alerts',
  'quality_report',
]);

function safeParse(value: string): unknown | null {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function stripMeta(value: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  Object.keys(value).forEach((key) => {
    if (!META_KEYS.has(key)) cleaned[key] = value[key];
  });
  return cleaned;
}

function normalizeAlerts(value: unknown): IterationAlert[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return { message: item };
      if (isPlainObject(item) && typeof item.message === 'string') {
        return {
          code: typeof item.code === 'string' ? item.code : undefined,
          message: item.message,
          severity: typeof item.severity === 'string' ? item.severity : undefined,
        };
      }
      return { message: '' };
    })
    .filter((item) => Boolean(item.message));
}

export function parseIterationResponse<T = Record<string, unknown>>(
  raw: unknown
): IterationResult<T> {
  let data: any = raw;

  if (typeof data === 'string') {
    const parsed = safeParse(data);
    if (isPlainObject(parsed)) {
      data = parsed;
    } else {
      return { reply: data, changed: false, alerts: [], alertDetails: [] };
    }
  }

  if (!isPlainObject(data)) {
    return {
      reply: typeof data === 'string' ? data : '',
      changed: false,
      alerts: [],
      alertDetails: [],
    };
  }

  // Compatibilidad con respuestas antiguas: { reply: "<json string>", ... }
  if (typeof data.reply === 'string' && data.reply.trim().startsWith('{')) {
    const inner = safeParse(data.reply);
    if (isPlainObject(inner)) {
      data = { ...data, ...inner };
    }
  }

  const reply =
    typeof data.reply === 'string' && !data.reply.trim().startsWith('{')
      ? data.reply
      : '';

  const stateSource = isPlainObject(data.module_state) ? data.module_state : data;
  const state = stripMeta(stateSource) as T;

  const history = Array.isArray(data.history)
    ? data.history
    : Array.isArray(stateSource.history)
      ? (stateSource.history as unknown[])
      : undefined;

  const alertDetails = normalizeAlerts(data.guardrail_alerts);
  const alerts = alertDetails.map((alert) => alert.message);
  const changed =
    data.changed !== false && (Object.keys(state as Record<string, unknown>).length > 0 || Boolean(reply));

  return { reply, state, history, changed, alerts, alertDetails };
}
